import React, { useState, useEffect, useRef } from "react";
import DragDrop from "../../DragDrop";
import { Pencil, Trash, WarningCircle } from "@phosphor-icons/react";
import axiosInstance from "../../../api/axiosInstance";
import PageLoader from "../../loader/PageLoader";
import ImageLoader from "../../loader/ImageLoader";
import {toast} from "react-toastify";
import { uploadImageToServer } from "../../../api/uploadImage";

const ConferenceRoomForm = () => {
    const [formData, setFormData] = useState({
        number: "",
        capacity: "",
        floor: "",
        photos: [],
    });
    const [showForm, setShowForm] = useState(false);
    const [conferenceRooms, setConferenceRooms] = useState([]);
    const [editIndex, setEditIndex] = useState(null);
    const [uploadedImageKeys, setUploadedImageKeys] = useState([]);
    const [uploadedImages, setUploadedImages] = useState([]);
    const [removedImageKeys, setRemovedImageKeys] = useState([]);
    const [isPageLoading, setIsPageLoading] = useState(true);
    const hotelId = localStorage.getItem("hotelId");
    const [errors, setErrors] = useState({});
    const imageRequestRef = useRef(0);

    useEffect(() => {
        fetchConferenceRooms();
    }, []);

    const fetchConferenceRooms = async () => {
        try {
            const response = await axiosInstance.get(
                `/api/v1/hotels/${hotelId}/conference-rooms`,
            );
            setConferenceRooms(response.data.data || []);
            setIsPageLoading(false);
        } catch (error) {
            console.error("Failed to fetch conference rooms:", error);
            if(error.response.data.message==="Hotel's conference rooms info not found"){
                setConferenceRooms([]);
            }
            setIsPageLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevFormData) => ({ ...prevFormData, [name]: value }));
        setErrors((prev) => ({ ...prev, [name]: "" }));
    };

    const resetFormStates = () => {
        setFormData({
            number: "",
            capacity: "",
            floor: "",
            photos: [],
        });
        setUploadedImageKeys([]);
        setUploadedImages([]);
        setRemovedImageKeys([]);
        setEditIndex(null);
        setErrors({});
    };

    const handleFileUpload = async (files) => {
        const fileArray = Array.from(files);
        // console.log("Files to upload:", fileArray);

        const placeholders = fileArray.map((file, index) => ({
            id: `temp_${Date.now()}_${index}`,
            isLoading: true,
            file: file,
        }));

        setUploadedImages((prevImages) => [...prevImages, ...placeholders]);

        const uploadPromises = placeholders.map(async (placeholder, index) => {
            try {
                // console.log(
                //     `Starting upload for file ${index}:`,
                //     placeholder.file.name,
                // );
                const uploadUrlResponse = await axiosInstance.get(
                    `/api/v1/images/upload-presigned-url?file_name=${placeholder.file.name}`,
                );
                const { key, url } = uploadUrlResponse.data;
                await uploadImageToServer(placeholder.file, url);
                console.log(
                    `Upload completed for file ${index}:`,
                    placeholder.file.name,
                );

                return {
                    id: placeholder.id,
                    key,
                    url: URL.createObjectURL(placeholder.file),
                    isLoading: false,
                };
            } catch (error) {
                console.error(`Error uploading image ${index}:`, error);
                return { ...placeholder, isLoading: false, error: true };
            }
        });

        const uploadedFiles = await Promise.all(uploadPromises);

        setUploadedImages((prevImages) =>
            prevImages.map(
                (img) => uploadedFiles.find((u) => u.id === img.id) || img,
            ),
        );

        const validFiles = uploadedFiles.filter((file) => !file.error);
        setUploadedImageKeys((prevKeys) => [
            ...prevKeys,
            ...validFiles.map((f) => ({ key: f.key })),
        ]);
        setErrors((prev) => ({ ...prev, photos: "" }));
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.number) newErrors.number = "Room number is required";
        if (!formData.capacity) newErrors.capacity = "Max capacity is required";
        if (!formData.floor) newErrors.floor = "Floor number is required";
        // if (uploadedImages.length === 0)
        //     newErrors.photos = "At least one image is required";
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleRemoveMediaFiles = (indexToRemove) => {
        setUploadedImages((prevImages) => {
            const newImages = prevImages.filter(
                (_, index) => index !== indexToRemove,
            );
            const removedImage = prevImages[indexToRemove];
            if (
                removedImage &&
                !removedImage.isLoading &&
                !removedImage.error
            ) {
                setRemovedImageKeys((prev) => [...prev, removedImage.key]);
            }
            return newImages;
        });
        setUploadedImageKeys((prevKeys) =>
            prevKeys.filter((_, index) => index !== indexToRemove),
        );
    };

    // Handle Submit
    const handleSaveRoom = async (e) => {
        e.preventDefault();
        if (!validateForm()) {
            return;
        }
        try {

            let finalFormData = {
                ...formData,
                capacity: parseInt(formData.capacity),
                floor: parseInt(formData.floor),
                number: parseInt(formData.number),
                photos: [],
            };

            if(formData.photos){
                const finalFormData = {
                    ...formData,
                    capacity: parseInt(formData.capacity),
                    floor: parseInt(formData.floor),
                    number: parseInt(formData.number),
                    photos: [
                        ...formData.photos.filter(
                            (photo) => !removedImageKeys.includes(photo),
                        ),
                        ...uploadedImageKeys.map((photo) => photo.key),
                    ],
                };
            }
       

            if (editIndex !== null) {
                await axiosInstance.put(
                    `/api/v1/hotels/${hotelId}/conference-rooms/${conferenceRooms[editIndex].id}`,
                    finalFormData,
                );
                // console.log("PUT - uploaded succesfully");
            } else {
                await axiosInstance.post(
                    `/api/v1/hotels/${hotelId}/conference-rooms`,
                    finalFormData,
                );
                // console.log("uploaded succesfully");
            }

            await fetchConferenceRooms();
            resetFormStates();
            setShowForm(false);
        } catch (error) {
            toast.error(error?.response?.data?.message || "Some Error Occured")
            console.error("Failed to save conference room:", error);
        }
    };


    // Hanlde Edit Button
    const handleEditClick = (index) => {
        const selectedRoom = conferenceRooms[index];
        setFormData(selectedRoom);
        setShowForm(true);
        setEditIndex(index);
        setRemovedImageKeys([]);
        fetchRoomImages(selectedRoom.photos);
    };

    const fetchRoomImages = async (imageKeys = []) => {
        // Two edits in quick succession leave both batches of presigned-URL calls in
        // flight with no ordering guarantee, so a slower earlier response could
        // overwrite a newer one. Only the most recent request may set state.
        const requestId = ++imageRequestRef.current;
        try{
            if(imageKeys){
                const imagePromises = imageKeys.map(async (key) => {
                    try {
                        const response = await axiosInstance.get(
                            `/api/v1/images/download-presigned-url?key=${key}`,
                        );
                        const imageUrl = response.data.url;
                        return { key, url: imageUrl };
                    } catch (error) {
                        console.error("Error fetching image URL:", error);
                        return null;
                    }
                });
        
                const images = await Promise.all(imagePromises);
                if (requestId !== imageRequestRef.current) return;
                setUploadedImages(images.filter((img) => img !== null));
            }

        }catch(error){
            console.log(error)

        }
    };

    // Handle Delete Button
    const handleDeleteClick = async (index) => {
        try {
            const roomId = conferenceRooms[index].id;
            await axiosInstance.delete(
                `/api/v1/hotels/${hotelId}/conference-rooms/${roomId}`,
            );
            await fetchConferenceRooms();
        } catch (error) {
            console.error("Error deleting conference room:", error);
        }
    };

    const trimImgName = (name) => {
        return name && name.length > 12
            ? name.substring(0, 12) + "..."
            : name || "";
    };

    return (
        <>
            {isPageLoading && <PageLoader />}
            <div className="flex w-full">
                <div className="border-r-[1.5px] px-4 w-[40%]">
                    <button
                        className={`h-[48px] ${
                            showForm
                                ? "bg-[#FFF2F2] text-[#FFBFC1] cursor-not-allowed"
                                : "bg-[#ff7378]"
                        } px-4 rounded-lg text-[16px] mb-3`}
                        onClick={() => {
                            setShowForm(true);
                            resetFormStates();
                        }}
                        disabled={showForm}
                    >
                        Add Conference Room
                    </button>

                    <div className="flex gap-2 flex-wrap">
                        {conferenceRooms.length > 0
                            ? conferenceRooms.map((room, index) => (
                                  <div
                                      className="border-[1.5px] rounded-md border-[#BFC4CC] bg-[#EEF0FB] flex gap-3 px-2 py-2 mb-2 w-full justify-between"
                                      key={index}
                                  >
                                      <p>Conference Room {room.number}</p>
                                      <div className="flex justify-end gap-1">
                                          <button
                                              className="rounded-md p-1 border-[1.5px] border-[#BFC4CC] h-[24px] w-[24px] bg-white"
                                              onClick={() =>
                                                  handleEditClick(index)
                                              }
                                          >
                                              <Pencil size={14} />
                                          </button>
                                          <button
                                              className="rounded-md p-1 border-[1.5px] border-[#BFC4CC] h-[24px] w-[24px] bg-white text-[#ff7378]"
                                              onClick={() =>
                                                  handleDeleteClick(index)
                                              }
                                          >
                                              <Trash size={14} />
                                          </button>
                                      </div>
                                  </div>
                              ))
                            : ""}
                    </div>
                </div>

                {showForm && (
                    <div className="w-[60%] px-4 self-start sticky top-0 max-h-[calc(100vh-80px)] overflow-y-auto">
                        <form onSubmit={handleSaveRoom}>
                            <button
                                className="h-[48px] bg-[#ff7378] w-full rounded-lg text-[16px]"
                                type="submit"
                            >
                                {editIndex !== null
                                    ? "Update Room"
                                    : "Add Room"}
                            </button>
                            <div className="flex flex-col gap-2 my-3">
                                <div>
                                    <p>Room</p>
                                    <input
                                        type="number"
                                        placeholder="Enter room number"
                                        className="w-[70%] p-2 border-[1.5px] rounded-lg mt-1"
                                        name="number"
                                        value={formData.number}
                                        onChange={handleChange}
                                    />
                                    {errors.number && (
                                        <span className="text-[#E47186] text-xs flex items-center gap-1">
                                            <WarningCircle className="text-sm" />
                                            {errors.number}
                                        </span>
                                    )}
                                </div>
                                <div>
                                    <p>Capacity</p>
                                    <input
                                        type="number"
                                        placeholder="Enter capacity"
                                        className="w-[70%] p-2 border-[1.5px] rounded-lg mt-1"
                                        name="capacity"
                                        value={formData.capacity}
                                        onChange={handleChange}
                                    />
                                    {errors.capacity && (
                                        <span className="text-[#E47186] text-xs flex items-center gap-1">
                                            <WarningCircle className="text-sm" />
                                            {errors.capacity}
                                        </span>
                                    )}
                                </div>
                                <div>
                                    <p>Floor</p>
                                    <input
                                        type="number"
                                        placeholder="Enter floor"
                                        className="w-[70%] p-2 border-[1.5px] rounded-lg mt-1"
                                        name="floor"
                                        value={formData.floor}
                                        onChange={handleChange}
                                    />
                                    {errors.floor && (
                                        <span className="text-[#E47186] text-xs flex items-center gap-1">
                                            <WarningCircle className="text-sm" />
                                            {errors.floor}
                                        </span>
                                    )}
                                </div>
                                <div>
                                    <p>Upload Media</p>
                                    <div>
                                        <DragDrop
                                            onFilesUploaded={handleFileUpload}
                                            uploadedImages={uploadedImages}
                                            onRemoveMediaFile={
                                                handleRemoveMediaFiles
                                            }
                                            acceptedFileTypes={{
                                                "image/*": [
                                                    ".jpeg",
                                                    ".png",
                                                    ".jpg",
                                                ],
                                            }}
                                            maxFiles={10}
                                            dragDropText="Drag and drop conference room images here"
                                        />
                                        {errors.photos && (
                                            <span className="text-[#E47186] text-xs flex items-center gap-1">
                                                <WarningCircle className="text-sm" />
                                                {errors.photos}
                                            </span>
                                        )}
                                    </div>
                                    <div>
                                        {uploadedImages.length > 0 && (
                                            <div className="flex gap-2 w-full mt-2 h-full flex-nowrap overflow-x-auto">
                                                {uploadedImages.map(
                                                    (photo, index) => (
                                                        <div
                                                            key={photo.id}
                                                            className="w-[158px] h-[142px] rounded-lg border-[1.5px] border-[#BFC4CC] flex-shrink-0 overflow-hidden"
                                                        >
                                                            {photo.isLoading ? (
                                                                <ImageLoader />
                                                            ) : photo.error ? (
                                                                <div className="w-full h-full flex items-center justify-center text-red-500">
                                                                    Upload
                                                                    failed
                                                                </div>
                                                            ) : (
                                                                <>
                                                                    <img
                                                                        src={
                                                                            photo.url
                                                                        }
                                                                        alt={`Conference Room ${
                                                                            index +
                                                                            1
                                                                        }`}
                                                                        className="w-full h-[110px] bg-cover"
                                                                    />
                                                                    <div className="flex justify-between items-center h-[32px] px-1 bg-[#EEF0FB]">
                                                                        <p>
                                                                            {trimImgName(
                                                                                photo.key,
                                                                            )}
                                                                        </p>
                                                                        <button
                                                                            className="rounded-md p-1 border-[1.5px] border-[#F8D9DE] text-[#DC4662] h-[24px] w-[24px] bg-white"
                                                                            onClick={() =>
                                                                                handleRemoveMediaFiles(
                                                                                    index,
                                                                                )
                                                                            }
                                                                            type="button"
                                                                        >
                                                                            <Trash
                                                                                size={
                                                                                    14
                                                                                }
                                                                            />
                                                                        </button>
                                                                    </div>
                                                                </>
                                                            )}
                                                        </div>
                                                    ),
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>
                )}
            </div>
        </>
    );
};

export default ConferenceRoomForm;
